/**
 * Drawing Mixin for Konva-based components
 * Provides shared functionality for Apple Pencil detection, drawing, and touch handling
 */

export default {
  data() {
    return {
      // Drawing State
      lines: [],
      currentDrawing: null,
      isPenActive: false,
      isDrawing: false,
      eraserMode: false,
      linesEdited: false,
      
      // Drawing Properties
      drawingProps: {
        stroke: "#000000",
        strokeWidth: 2,
        lineCap: "round",
        lineJoin: "round",
        tension: 0.5,
        globalCompositeOperation: "source-over",
      },
    };
  },

  mounted() {
    this.initializeDrawing();
  },

  beforeDestroy() {
    this.cleanupDrawing();
  },

  methods: {
    // === INITIALIZATION ===
    initializeDrawing() {
      // Setup pointer events for Apple Pencil detection
      window.addEventListener("pointerdown", this.detectApplePencil);
    },

    cleanupDrawing() {
      window.removeEventListener("pointerdown", this.detectApplePencil);
    },

    // === APPLE PENCIL DETECTION ===
    detectApplePencil(e) {
      // Detect Apple Pencil via pointer events
      const wasActive = this.isPenActive;
      
      if (e.pointerType === "pen") {
        this.isPenActive = true;
      } else if (e.pointerType === "touch" || e.pointerType === "mouse") {
        this.isPenActive = false;
      }
      
      // Force update stages when pen state changes
      if (wasActive !== this.isPenActive) {
        this.$nextTick(() => {
          this.updateStageListening();
        });
      }
    },

    updateStageListening() {
      // Update all stages when pen state changes
      Object.keys(this.$refs).forEach(refKey => {
        if (refKey.startsWith("stage")) {
          const vueStage = this.$refs[refKey][0];
          if (vueStage && vueStage.getStage) {
            const stage = vueStage.getStage();
            if (stage) {
              stage.listening(this.isPenActive);
              
              // Also directly manipulate canvas pointer events
              const canvas = stage.getContent();
              if (canvas) {
                canvas.style.pointerEvents = this.isPenActive ? 'auto' : 'none';
                canvas.style.touchAction = this.isPenActive ? 'none' : 'auto';
              }
            }
          }
        }
      });
    },

    // === EVENT HANDLING ===
    handleMouseDown(e, pageIndex) {
      // Only allow drawing with mouse if no touch is active
      if (!this.isPenActive) {
        if (this.eraserMode) {
          this.startObjEraser(e, pageIndex);
        } else {
          this.startDrawing(e, pageIndex);
        }
      }
    },

    handleTouchStart(e, pageIndex) {
      // Check if it's Apple Pencil
      if (this.isPenActive) {
        e.evt.preventDefault(); // Prevent scrolling for Apple Pencil
        if (this.eraserMode) {
          this.startObjEraser(e, pageIndex);
        } else {
          this.startDrawing(e, pageIndex);
        }
      }
      // For finger touches, don't prevent default to allow scrolling
    },

    handleMouseMove(e, pageIndex) {
      if (this.isDrawing && !this.isPenActive) {
        if (this.eraserMode) {
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        } else {
          this.continueDrawing(e, pageIndex);
        }
      }
    },

    handleTouchMove(e, pageIndex) {
      if (this.isDrawing && this.isPenActive) {
        if (this.eraserMode) {
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        } else {
          this.continueDrawing(e, pageIndex);
        }
      }
      // For finger touches, don't prevent default to allow scrolling
    },

    handleMouseUp(e, pageIndex) {
      if (this.isDrawing && !this.isPenActive) {
        if (this.eraserMode) {
          this.lines[pageIndex].pop();
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
      }
    },

    handleTouchEnd(e, pageIndex) {
      if (this.isDrawing && this.isPenActive) {
        if (this.eraserMode) {
          this.lines[pageIndex].pop();
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
      }
    },

    // === DRAWING METHODS ===
    startDrawing(e, pageIndex) {
      const stage = e.target.getStage();
      const pos = stage.getPointerPosition();

      if (!pos) return;

      this.isDrawing = true;
      this.currentDrawing = {
        ...this.drawingProps,
        points: [this.roundCoordinate(pos.x), this.roundCoordinate(pos.y)],
      };

      // Ensure lines array exists for this page
      if (!this.lines[pageIndex]) {
        this.$set(this.lines, pageIndex, []);
      }

      this.lines[pageIndex].push(this.currentDrawing);
    },

    continueDrawing(e, pageIndex) {
      if (!this.isDrawing || !this.currentDrawing) return;

      const stage = e.target.getStage();
      const pos = stage.getPointerPosition();

      if (!pos) return;

      // Add new point to current line with rounded coordinates
      this.currentDrawing.points = this.currentDrawing.points.concat([
        this.roundCoordinate(pos.x),
        this.roundCoordinate(pos.y),
      ]);

      // Update the line in the array
      const lineIndex = this.lines[pageIndex].length - 1;
      this.$set(this.lines[pageIndex], lineIndex, { ...this.currentDrawing });
    },

    finishDrawing() {
      if (!this.isDrawing || !this.currentDrawing) return;

      // Simplify line for better performance
      if (this.currentDrawing.points.length > 4) {
        const points = this.chunkArrayInGroups(this.currentDrawing.points, 2);
        const simplifiedPoints = this.simplifyLineRDP(points, 1.5);
        this.currentDrawing.points = [].concat.apply([], simplifiedPoints);
      } else {
        // Round coordinates even for short lines
        const roundedPoints = [];
        for (let i = 0; i < this.currentDrawing.points.length; i += 2) {
          roundedPoints.push(
            this.roundCoordinate(this.currentDrawing.points[i]),
            this.roundCoordinate(this.currentDrawing.points[i + 1])
          );
        }
        this.currentDrawing.points = roundedPoints;
      }

      this.isDrawing = false;
      this.currentDrawing = null;
    },

    // === DRAWING UTILITIES ===
    async clearAllDrawings() {
      // This method should be overridden by components to handle their specific clearing logic
      this.lines = [];
    },

    addHistory() {
      // This method should be implemented by components that need history tracking
      // Default implementation does nothing
    },

    initializeLinesForPage(pageIndex) {
      if (!this.lines[pageIndex]) {
        this.$set(this.lines, pageIndex, []);
      }
    },

    // === UTILITY METHODS ===
    chunkArrayInGroups(arr, size) {
      if (!arr || !Array.isArray(arr)) return [];
      const result = [];
      for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
      }
      return result;
    },

    roundCoordinate(value) {
      // Round coordinates to 1 decimal place to reduce file size and improve performance
      return Math.round(value * 10) / 10;
    },

    roundPoint(point) {
      if (!point || point.length < 2) return point;
      return [
        this.roundCoordinate(point[0]),
        this.roundCoordinate(point[1])
      ];
    },

    simplifyLineRDP(points, tolerance) {
      if (!points || points.length < 2) return points;

      const simplify = (start, end) => {
        let maxDist = 0;
        let index = 0;

        const p1 = points[start];
        const p2 = points[end];

        for (let i = start + 1; i < end; i++) {
          const point = points[i];
          const dist = this.getPerpendicularDistance(point, p1, p2);

          if (dist > maxDist) {
            index = i;
            maxDist = dist;
          }
        }

        if (maxDist > tolerance) {
          const recResults1 = simplify(start, index);
          const recResults2 = simplify(index, end);

          return recResults1
            .slice(0, recResults1.length - 1)
            .concat(recResults2);
        } else {
          return [this.roundPoint(p1), this.roundPoint(p2)];
        }
      };

      const simplifiedPoints = simplify(0, points.length - 1);
      
      // Round all coordinates in the final result
      return simplifiedPoints.map(point => this.roundPoint(point));
    },

    getPerpendicularDistance(point, lineStart, lineEnd) {
      const dx = lineEnd[0] - lineStart[0];
      const dy = lineEnd[1] - lineStart[1];

      if (dx === 0 && dy === 0) {
        return Math.sqrt(
          Math.pow(point[0] - lineStart[0], 2) +
            Math.pow(point[1] - lineStart[1], 2)
        );
      }

      const t =
        ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) /
        (dx * dx + dy * dy);

      if (t < 0) {
        return Math.sqrt(
          Math.pow(point[0] - lineStart[0], 2) +
            Math.pow(point[1] - lineStart[1], 2)
        );
      } else if (t > 1) {
        return Math.sqrt(
          Math.pow(point[0] - lineEnd[0], 2) +
            Math.pow(point[1] - lineEnd[1], 2)
        );
      } else {
        const projX = lineStart[0] + t * dx;
        const projY = lineStart[1] + t * dy;
        return Math.sqrt(
          Math.pow(point[0] - projX, 2) + Math.pow(point[1] - projY, 2)
        );
      }
    },

    // === ERASING METHODS ===
    startObjEraser(e, pageIndex) {
      if (this.eraserMode) {
        this.isDrawing = true;
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();

        if (!pos) {
          return;
        }

        this.currentDrawing = {
          ...this.drawingProps,
          stroke: "gainsboro",
          strokeWidth: 5,
          bezier: true,
          points: [this.roundCoordinate(pos.x), this.roundCoordinate(pos.y)],
        };

        // Ensure lines array exists for this page
        if (!this.lines[pageIndex]) {
          this.$set(this.lines, pageIndex, []);
        }

        this.lines[pageIndex].push(this.currentDrawing);
      }
    },

    objErasing(pageIndex) {
      if (!this.lines[pageIndex] || !this.currentDrawing) return;

      let length = 50;
      this.currentDrawing.points.splice(
        -length - 2,
        this.currentDrawing.points.length - length
      );
      let eraserPoints = this.currentDrawing.points.slice(-4);
      
      this.lines[pageIndex].forEach((line, index) => {
        if (this.lines[pageIndex].length - 1 === index) return;
        let points = [...line.points];
        while (points.length >= 4) {
          let linePoints = points.slice(0, 4);
          let int = this.intersect(...eraserPoints, ...linePoints);
          if (int) {
            this.lines[pageIndex].splice(index, 1);
            this.linesEdited = true;
            break;
          }
          // Move to the next segment of the line
          points.splice(0, 2);
        }
      });

      // Check if all lines on the page are erased
      if (this.lines[pageIndex].length === 0) {
        // Remove the empty lines entry
        this.lines.splice(pageIndex, 1);

        // Remove the corresponding page if pages array exists
        if (this.pages && this.pages.length > pageIndex) {
          this.pages.splice(pageIndex, 1);
        }
      }
    },

    intersect(x1, y1, x2, y2, x3, y3, x4, y4) {
      if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
        return false;
      }
      let denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
      if (Math.abs(denominator) < 1e-10) {
        return false;
      }
      let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
      let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
      if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
        return false;
      }
      let x = x1 + ua * (x2 - x1);
      let y = y1 + ua * (y2 - y1);
      return { x, y };
    },

    // === STAGE CONFIGURATION ===
    getDrawingStageConfig(baseConfig = {}) {
      return {
        ...baseConfig,
        // Disable Konva event handling when not using Apple Pencil
        listening: this.isPenActive,
      };
    },
  },
};